import { apiClient } from './api';
import { authService } from './authService';

export interface Project {
  id: string;
  name: string;
  description: string | null;
  source_type: 'github' | 'local' | 'gitlab' | 'bitbucket';
  source_location: string;
  source_branch: string;
  analysis_status: 'created' | 'analyzing' | 'analyzed' | 'modernizing' | 'completed' | 'failed';
  frontend_stack: TechStackInfo[] | null;
  backend_stack: TechStackInfo[] | null;
  database_stack: TechStackInfo[] | null;
  infrastructure_stack: TechStackInfo[] | null;
  analysis_results?: {
    graph_job_id?: string;
    app_id?: string;
    total_nodes?: number;
    total_relationships?: number;
    languages_detected?: string[];
    node_labels?: Record<string, number>;
    error_message?: string;
  };
  created_by: string;
  created_at: string;
  updated_at: string;
  analyzed_at: string | null;
}

export interface TechStackInfo {
  name: string;
  version?: string;
  files: string[];
  confidence: number;
}

export interface ProjectListResponse {
  total: number;
  page: number;
  page_size: number;
  projects: Project[];
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  source_type: 'github' | 'local' | 'gitlab' | 'bitbucket';
  source_location: string;
  source_branch?: string;
}

export interface AnalysisResponse {
  project_id: string;
  total_files: number;
  total_lines: number;
  languages: Record<string, number>;
  frameworks: string[];
  frontend_stack: TechStackInfo[];
  backend_stack: TechStackInfo[];
  database_stack: TechStackInfo[];
  infrastructure_stack: TechStackInfo[];
  dependencies: Record<string, any>;
  recommendations: string[];
  analyzed_at: string;
}

/**
 * Project service for API calls
 */
export const projectService = {
  /**
   * Get list of projects
   */
  async getProjects(page = 1, pageSize = 10): Promise<ProjectListResponse> {
    const response = await apiClient.get<ProjectListResponse>('/projects', {
      params: { page, page_size: pageSize },
    });
    return response.data;
  },

  /**
   * Get single project by ID
   */
  async getProject(id: string): Promise<Project> {
    const response = await apiClient.get<Project>(`/projects/${id}`);
    return response.data;
  },

  /**
   * Create new project
   */
  async createProject(data: CreateProjectRequest): Promise<Project> {
    const response = await apiClient.post<Project>('/projects', data);
    return response.data;
  },

  /**
   * Update project
   */
  async updateProject(id: string, data: Partial<CreateProjectRequest>): Promise<Project> {
    const response = await apiClient.patch<Project>(`/projects/${id}`, data);
    return response.data;
  },

  /**
   * Delete project
   */
  async deleteProject(id: string): Promise<void> {
    await apiClient.delete(`/projects/${id}`);
  },

  /**
   * Start code analysis
   */
  async startAnalysis(projectId: string, deepAnalysis = false): Promise<{ message: string; project_id: string; status: string }> {
    const response = await apiClient.post('/analysis/start', {
      project_id: projectId,
      deep_analysis: deepAnalysis,
    });
    return response.data;
  },

  /**
   * Get analysis results
   */
  async getAnalysisResults(projectId: string): Promise<AnalysisResponse> {
    const response = await apiClient.get<AnalysisResponse>(`/analysis/${projectId}`);
    return response.data;
  },

  /**
   * Check analysis job status from code-graph-service
   */
  async checkJobStatus(jobId: string): Promise<{
    status: 'pending' | 'cloning' | 'parsing' | 'building_graph' | 'completed' | 'failed';
    progress?: {
      files_total?: number;
      files_processed?: number;
      nodes_created?: number;
      relationships_created?: number;
      languages_detected?: string[];
      current_file?: string;
    };
    error_message?: string;
  }> {
    const codeGraphUrl = import.meta.env.VITE_CODE_GRAPH_SERVICE_URL || 'http://localhost:8001/api/v1';
    const token = authService.getToken();

    // Use fetch instead of apiClient because we need to call a different service
    const response = await fetch(`${codeGraphUrl}/analyze/${jobId}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to check job status: ${response.statusText}`);
    }

    return await response.json();
  },

  /**
   * Update analysis status in core-platform-service
   */
  async updateAnalysisStatus(
    projectId: string,
    status: 'completed' | 'failed',
    graphStats?: any,
    errorMessage?: string
  ): Promise<void> {
    await apiClient.patch(`/analysis/${projectId}/status`, {
      status,
      graph_stats: graphStats,
      error_message: errorMessage,
    });
  },
};
